import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Github, Linkedin, Mail } from 'lucide-react';
import { personalInfo, contactData } from '../data/personalData';

const roles = [
  'Data Analyst',
  'SQL & Python',
  'ETL Pipelines',
  'Power BI Dashboards',
];

const useTypedRole = (words: string[]) => {
  const [text, setText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[wordIndex % words.length];
    const speed = deleting ? 35 : 65;
    const pause = !deleting && text === current ? 1200 : deleting && text === '' ? 300 : speed;

    const timer = setTimeout(() => {
      if (!deleting) {
        if (text.length < current.length) {
          setText(current.slice(0, text.length + 1));
        } else {
          setDeleting(true);
        }
      } else {
        if (text.length > 0) {
          setText(current.slice(0, text.length - 1));
        } else {
          setDeleting(false);
          setWordIndex((i) => i + 1);
        }
      }
    }, pause);

    return () => clearTimeout(timer);
  }, [text, deleting, wordIndex, words]);

  return text;
};

export const Hero: React.FC = () => {
  const typed = useTypedRole(roles);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-20 pb-16 px-6 bg-dotgrid overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background pointer-events-none" />

      <div className="relative max-w-5xl mx-auto w-full grid md:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-mono text-sm text-accent mb-3">$ whoami</p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-text leading-[1.1] mb-4">
            {personalInfo.name}
          </h1>
          <div className="font-mono text-lg sm:text-xl text-accent2 h-8 mb-6">
            {typed}
            <span className="inline-block w-2 h-5 bg-accent2 ml-1 align-middle animate-blink" />
          </div>
          <p className="text-muted text-base sm:text-lg max-w-xl mb-8 leading-relaxed">
            {personalInfo.bio}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href="#projects"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-5 py-2.5 bg-accent text-background font-mono text-sm font-semibold rounded hover:bg-accent/90 transition-colors"
            >
              view_projects()
            </a>
            <a
              href="/Aditya_Bharti_Data_Analyst.pdf"
              download
              className="px-5 py-2.5 border border-border text-text font-mono text-sm rounded hover:border-accent hover:text-accent transition-colors"
            >
              download_resume()
            </a>
          </div>

          <div className="flex items-center gap-4 mt-8">
            <a
              href={contactData.profiles.github}
              target="_blank"
              rel="noreferrer"
              className="text-muted hover:text-accent transition-colors"
              aria-label="GitHub"
            >
              <Github size={19} />
            </a>
            <a
              href={personalInfo.linkedin}
              target="_blank"
              rel="noreferrer"
              className="text-muted hover:text-accent transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin size={19} />
            </a>
            <a
              href={`mailto:${personalInfo.email}`}
              className="text-muted hover:text-accent transition-colors"
              aria-label="Email"
            >
              <Mail size={19} />
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="order-first md:order-last w-full max-w-[300px] sm:max-w-sm md:max-w-none mx-auto"
        >
          <div className="relative">
            {/* warm glow behind the frame */}
            <div className="absolute -inset-6 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

            <div className="relative rounded-lg border border-border bg-surface overflow-hidden shadow-2xl shadow-black/50">
              <div className="flex items-center gap-1.5 px-4 py-2.5 bg-raised border-b border-border">
                <span className="w-2.5 h-2.5 rounded-full bg-danger/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-accent/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-accent2/70" />
                <span className="ml-3 text-xs font-mono text-muted">aditya.webp</span>
              </div>

              <div className="relative aspect-[1/1] bg-gradient-to-b from-raised via-surface to-surface overflow-hidden">
                {/* faint grid + amber light behind the head */}
                <div className="absolute inset-0 bg-dotgrid opacity-60" />
                <div className="absolute left-1/2 top-[8%] -translate-x-1/2 w-[70%] aspect-square rounded-full bg-accent/15 blur-3xl" />

                <picture>
                  <source srcSet="/profile.webp" type="image/webp" />
                  <img
                    src="/profile.webp"
                    alt="Portrait of Aditya Bharti"
                    width={900}
                    height={878}
                    className="absolute bottom-0 left-0 w-full h-full object-contain object-bottom select-none"
                    draggable={false}
                  />
                </picture>

                {/* corner brackets */}
                <span className="absolute top-3 left-3 w-4 h-4 border-t border-l border-accent/70" />
                <span className="absolute top-3 right-3 w-4 h-4 border-t border-r border-accent/70" />
                <span className="absolute bottom-3 left-3 w-4 h-4 border-b border-l border-accent/70" />
                <span className="absolute bottom-3 right-3 w-4 h-4 border-b border-r border-accent/70" />

                {/* query result chip */}
                <div className="absolute bottom-3 left-3 right-3 sm:right-auto sm:max-w-[88%] rounded border border-border/80 bg-background/80 backdrop-blur-sm px-3 py-2 font-mono text-[10px] sm:text-xs leading-relaxed">
                  <p className="text-muted">
                    <span className="text-accent2">SELECT</span> name, role{' '}
                    <span className="text-accent2">FROM</span> engineers;
                  </p>
                  <p className="text-text">
                    Aditya Bharti <span className="text-muted">|</span> Data Analyst{' '}
                    <span className="text-muted">(1 row)</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <ArrowDown className="text-muted animate-bounce" size={18} />
      </motion.div>
    </section>
  );
};
